/**
 * ContentIntelligence
 *
 * Content Intelligence Dashboard — 4 connected modules forming a funnel:
 * 1. Expert Interview Opportunities
 * 2. Content Opportunity Planner
 * 3. E-E-A-T Quality Map (placeholder)
 * 4. Achievements & Progression (placeholder)
 *
 * Date: 2026-04-04
 */

import { useState, useEffect, useCallback } from 'react';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Lightbulb,
  MessageSquare,
  Shield,
  Trophy,
  RefreshCw,
  Loader2,
  TrendingUp,
  Target,
  Users,
  Zap,
  BookOpen,
  BarChart3,
  ArrowRight,
} from 'lucide-react';

interface Opportunity {
  id: string;
  question?: string;
  title?: string;
  opportunityScore: number;
  scores: {
    volume: number;
    qualityGap: number;
    competitiveGap: number;
    eeatGap?: number;
    demandVelocity?: number;
  };
  category?: string;
  search_impressions?: number;
  search_clicks?: number;
  competitor_count?: number;
  has_expert_answer?: boolean;
  suggested_expert_profile?: string;
  content_type?: string;
  coverage_ratio?: number;
  commercial_value_eur?: number;
  demand_velocity?: string;
  suggested_approach?: string;
  computedAt?: string;
}

interface EEATCategory {
  category: string;
  categoryName: string;
  productCount: number;
  scores: { experience: number; expertise: number; authority: number; trust: number; overall: number };
  metrics: { expertContentCount: number; evalCount: number; avgQuality: number | null; publishedCount: number };
  recommendations: { dimension: string; recommendation: string }[];
  lowestDimension: string;
}

interface Achievement {
  id: string;
  title: string;
  description: string;
  target: number;
  current: number;
  unlocked: boolean;
  icon: string;
}

interface CacheStatus {
  [key: string]: { count: number; latestComputation: string };
}

export default function ContentIntelligence() {
  const { selectedSite } = useSite();
  const [activeTab, setActiveTab] = useState('expert');
  const [expertOpportunities, setExpertOpportunities] = useState<Opportunity[]>([]);
  const [contentOpportunities, setContentOpportunities] = useState<Opportunity[]>([]);
  const [eeatCategories, setEeatCategories] = useState<EEATCategory[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [achievementSummary, setAchievementSummary] = useState<any>(null);
  const [progressionData, setProgressionData] = useState<any>(null);
  const [cacheStatus, setCacheStatus] = useState<CacheStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const siteId = selectedSite?.id;

  const fetchData = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    setError(null);

    try {
      const [expertRes, contentRes, statusRes, eeatRes, achieveRes, progressRes] = await Promise.all([
        authFetch(`/api/content-intelligence/expert-opportunities/${siteId}?limit=20`),
        authFetch(`/api/content-intelligence/content-opportunities/${siteId}?limit=20`),
        authFetch(`/api/content-intelligence/cache-status/${siteId}`),
        authFetch(`/api/content-intelligence/eeat-map/${siteId}`),
        authFetch(`/api/content-intelligence/achievements/${siteId}`),
        authFetch(`/api/content-intelligence/progression/${siteId}?months=3`),
      ]);

      if (expertRes.ok) {
        const data = await expertRes.json();
        setExpertOpportunities(data.opportunities || []);
      }
      if (contentRes.ok) {
        const data = await contentRes.json();
        setContentOpportunities(data.opportunities || []);
      }
      if (statusRes.ok) {
        setCacheStatus(await statusRes.json());
      }
      if (eeatRes.ok) {
        const data = await eeatRes.json();
        setEeatCategories(data.categories || []);
      }
      if (achieveRes.ok) {
        const data = await achieveRes.json();
        setAchievements(data.achievements || []);
        setAchievementSummary(data.summary || null);
      }
      if (progressRes.ok) {
        setProgressionData(await progressRes.json());
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load content intelligence data');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRecompute = async () => {
    if (!siteId) return;
    setRecomputing(true);
    try {
      const res = await authFetch(`/api/content-intelligence/recompute/${siteId}`, {
        method: 'POST',
      });
      if (res.ok) {
        await fetchData();
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setRecomputing(false);
    }
  };

  const cacheAge = cacheStatus?.question?.latestComputation
    ? Math.floor((Date.now() - new Date(cacheStatus.question.latestComputation).getTime()) / 3600000)
    : null;

  if (!siteId) {
    return (
      <div className="p-6">
        <Alert><AlertDescription>Select a site to view content intelligence.</AlertDescription></Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Content Intelligence</h1>
          <p className="text-muted-foreground">
            Discover the highest-value content opportunities for your site
          </p>
        </div>
        <div className="flex items-center gap-3">
          {cacheAge !== null && (
            <span className="text-sm text-muted-foreground">
              Updated {cacheAge < 1 ? 'just now' : `${cacheAge}h ago`}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleRecompute}
            disabled={recomputing}
          >
            {recomputing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Recompute
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="expert" className="flex items-center gap-1.5">
            <MessageSquare className="h-4 w-4" />
            Expert Opportunities
          </TabsTrigger>
          <TabsTrigger value="planner" className="flex items-center gap-1.5">
            <Lightbulb className="h-4 w-4" />
            Content Planner
          </TabsTrigger>
          <TabsTrigger value="eeat" className="flex items-center gap-1.5">
            <Shield className="h-4 w-4" />
            E-E-A-T Map
          </TabsTrigger>
          <TabsTrigger value="achievements" className="flex items-center gap-1.5">
            <Trophy className="h-4 w-4" />
            Achievements
          </TabsTrigger>
        </TabsList>

        {/* Module 1: Expert Interview Opportunities */}
        <TabsContent value="expert" className="space-y-4">
          {loading ? (
            <LoadingSkeleton />
          ) : error ? (
            <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>
          ) : expertOpportunities.length === 0 ? (
            <EmptyState
              icon={<MessageSquare className="h-12 w-12 text-muted-foreground" />}
              title="No expert opportunities yet"
              description="Recompute the opportunity graph to discover questions that would benefit from human expertise."
            />
          ) : (
            <>
              <div className="grid grid-cols-3 gap-4">
                <StatCard
                  title="Top Opportunities"
                  value={expertOpportunities.length}
                  icon={<Target className="h-4 w-4" />}
                />
                <StatCard
                  title="Avg Opportunity Score"
                  value={Math.round(expertOpportunities.reduce((s, o) => s + o.opportunityScore, 0) / expertOpportunities.length)}
                  suffix="/100"
                  icon={<BarChart3 className="h-4 w-4" />}
                />
                <StatCard
                  title="With Expert Answers"
                  value={expertOpportunities.filter(o => o.has_expert_answer).length}
                  suffix={`/${expertOpportunities.length}`}
                  icon={<Users className="h-4 w-4" />}
                />
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Expert Interview Opportunities</CardTitle>
                  <CardDescription>Questions ranked by value — focus on the top ones for maximum impact</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[40%]">Question</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead className="text-center">Score</TableHead>
                        <TableHead className="text-center">Volume</TableHead>
                        <TableHead className="text-center">Competition</TableHead>
                        <TableHead>Expert Profile</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {expertOpportunities.map((opp) => (
                        <TableRow key={opp.id}>
                          <TableCell>
                            <div className="font-medium text-sm">{opp.question}</div>
                            {opp.has_expert_answer && (
                              <Badge variant="secondary" className="mt-1 text-xs">Has expert answer</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">{opp.category || 'General'}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <ScoreBadge score={opp.opportunityScore} />
                          </TableCell>
                          <TableCell className="text-center text-sm text-muted-foreground">
                            {opp.search_impressions ? opp.search_impressions.toLocaleString() : '-'}
                          </TableCell>
                          <TableCell className="text-center">
                            {opp.competitor_count ? (
                              <span className="text-sm">{opp.competitor_count} competitors</span>
                            ) : '-'}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                            {opp.suggested_expert_profile || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Module 2: Content Opportunity Planner */}
        <TabsContent value="planner" className="space-y-4">
          {loading ? (
            <LoadingSkeleton />
          ) : error ? (
            <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>
          ) : contentOpportunities.length === 0 ? (
            <EmptyState
              icon={<Lightbulb className="h-12 w-12 text-muted-foreground" />}
              title="No content opportunities yet"
              description="Recompute the opportunity graph to discover content creation opportunities."
            />
          ) : (
            <>
              <div className="grid grid-cols-3 gap-4">
                <StatCard
                  title="Opportunities Found"
                  value={contentOpportunities.length}
                  icon={<Lightbulb className="h-4 w-4" />}
                />
                <StatCard
                  title="Content Types"
                  value={new Set(contentOpportunities.map(o => o.content_type)).size}
                  icon={<BookOpen className="h-4 w-4" />}
                />
                <StatCard
                  title="Avg Score"
                  value={Math.round(contentOpportunities.reduce((s, o) => s + o.opportunityScore, 0) / contentOpportunities.length)}
                  suffix="/100"
                  icon={<TrendingUp className="h-4 w-4" />}
                />
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Content Creation Opportunities</CardTitle>
                  <CardDescription>Recommended content pieces ranked by expected value</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[35%]">Recommended Content</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-center">Score</TableHead>
                        <TableHead className="text-center">Volume</TableHead>
                        <TableHead className="text-center">Gap</TableHead>
                        <TableHead>Approach</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {contentOpportunities.map((opp) => (
                        <TableRow key={opp.id}>
                          <TableCell>
                            <div className="font-medium text-sm">{opp.title}</div>
                            {opp.coverage_ratio !== undefined && (
                              <div className="flex items-center gap-2 mt-1">
                                <Progress value={opp.coverage_ratio} className="h-1.5 w-16" />
                                <span className="text-xs text-muted-foreground">{opp.coverage_ratio}% covered</span>
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <ContentTypeBadge type={opp.content_type} />
                          </TableCell>
                          <TableCell className="text-center">
                            <ScoreBadge score={opp.opportunityScore} />
                          </TableCell>
                          <TableCell className="text-center text-sm">
                            {opp.commercial_value_eur
                              ? `${Math.round(opp.commercial_value_eur).toLocaleString()}`
                              : opp.scores.volume}
                          </TableCell>
                          <TableCell className="text-center text-sm">
                            {opp.scores.qualityGap}
                          </TableCell>
                          <TableCell>
                            <ApproachBadge approach={opp.suggested_approach} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Module 3: E-E-A-T Quality Map */}
        <TabsContent value="eeat" className="space-y-4">
          {loading ? (
            <LoadingSkeleton />
          ) : eeatCategories.length === 0 ? (
            <EmptyState
              icon={<Shield className="h-12 w-12 text-muted-foreground" />}
              title="No E-E-A-T data yet"
              description="E-E-A-T scores require published content and quality evaluations. Recompute the graph after content is generated."
            />
          ) : (
            <>
              <div className="grid grid-cols-4 gap-4">
                {['experience', 'expertise', 'authority', 'trust'].map((dim) => {
                  const avg = eeatCategories.reduce((s, c) => s + (c.scores as any)[dim], 0) / eeatCategories.length;
                  return (
                    <Card key={dim}>
                      <CardContent className="pt-6">
                        <p className="text-sm text-muted-foreground capitalize">{dim}</p>
                        <p className="text-2xl font-bold">{avg.toFixed(1)}<span className="text-sm font-normal text-muted-foreground">/10</span></p>
                        <Progress value={avg * 10} className="mt-2 h-1.5" />
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Category E-E-A-T Scores</CardTitle>
                  <CardDescription>Categories sorted by overall score (lowest first) — focus improvement here</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[25%]">Category</TableHead>
                        <TableHead className="text-center">Experience</TableHead>
                        <TableHead className="text-center">Expertise</TableHead>
                        <TableHead className="text-center">Authority</TableHead>
                        <TableHead className="text-center">Trust</TableHead>
                        <TableHead className="text-center">Overall</TableHead>
                        <TableHead>Top Recommendation</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {eeatCategories.map((cat) => (
                        <TableRow key={cat.category}>
                          <TableCell>
                            <div className="font-medium text-sm">{cat.categoryName}</div>
                            <span className="text-xs text-muted-foreground">{cat.metrics.publishedCount} published</span>
                          </TableCell>
                          {(['experience', 'expertise', 'authority', 'trust'] as const).map((dim) => (
                            <TableCell key={dim} className="text-center">
                              <DimensionScore score={(cat.scores as any)[dim]} highlight={cat.lowestDimension === dim} />
                            </TableCell>
                          ))}
                          <TableCell className="text-center">
                            <Badge variant={cat.scores.overall >= 6 ? 'default' : cat.scores.overall >= 4 ? 'secondary' : 'destructive'}>
                              {cat.scores.overall}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground max-w-[250px]">
                            {cat.recommendations[0]?.recommendation || 'No action needed'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Module 4: Achievements & Progression */}
        <TabsContent value="achievements" className="space-y-4">
          {loading ? (
            <LoadingSkeleton />
          ) : (
            <>
              {/* Achievement badges */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="h-5 w-5" />
                    Achievements
                    {achievementSummary && (
                      <Badge variant="secondary">{achievementSummary.unlocked}/{achievementSummary.total} unlocked</Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-3">
                    {achievements.map((a) => (
                      <div
                        key={a.id}
                        className={`rounded-lg border p-4 text-center ${a.unlocked ? 'bg-primary/5 border-primary/30' : 'opacity-50'}`}
                      >
                        <div className="text-2xl mb-1">{a.unlocked ? getAchievementEmoji(a.icon) : '🔒'}</div>
                        <p className="font-medium text-sm">{a.title}</p>
                        <p className="text-xs text-muted-foreground mt-1">{a.description}</p>
                        <Progress value={Math.min(100, (a.current / a.target) * 100)} className="mt-2 h-1" />
                        <p className="text-xs text-muted-foreground mt-1">{a.current} / {a.target}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Summary stats */}
              {achievementSummary && (
                <div className="grid grid-cols-4 gap-4">
                  <StatCard title="Published" value={achievementSummary.published} icon={<BookOpen className="h-4 w-4" />} />
                  <StatCard title="Avg Quality" value={achievementSummary.avgQuality} suffix="/10" icon={<Shield className="h-4 w-4" />} />
                  <StatCard title="Deploy Rate" value={achievementSummary.deploymentRate} suffix="%" icon={<Target className="h-4 w-4" />} />
                  <StatCard title="ROI Impact" value={Math.round(achievementSummary.totalRoiDeltaEur)} suffix=" EUR" icon={<TrendingUp className="h-4 w-4" />} />
                </div>
              )}

              {/* Progression charts (data points as simple table since we don't add Recharts dependency) */}
              {progressionData && progressionData.published?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Content Progression (Last 3 Months)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-sm font-medium mb-2">Weekly Published</h4>
                        <div className="space-y-1">
                          {progressionData.published.slice(-8).map((p: any, i: number) => (
                            <div key={i} className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground w-20">{new Date(p.week).toLocaleDateString('en', { month: 'short', day: 'numeric' })}</span>
                              <div className="flex-1 bg-muted rounded-full h-2">
                                <div className="bg-primary rounded-full h-2" style={{ width: `${Math.min(100, p.count / 2)}%` }} />
                              </div>
                              <span className="text-xs font-medium w-8 text-right">{p.count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium mb-2">Quality Trend</h4>
                        <div className="space-y-1">
                          {progressionData.quality.slice(-8).map((q: any, i: number) => (
                            <div key={i} className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground w-20">{new Date(q.week).toLocaleDateString('en', { month: 'short', day: 'numeric' })}</span>
                              <div className="flex-1 bg-muted rounded-full h-2">
                                <div className="bg-green-500 rounded-full h-2" style={{ width: `${q.avgScore * 10}%` }} />
                              </div>
                              <span className="text-xs font-medium w-8 text-right">{q.avgScore}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Subcomponents ─────────────────────────────────────────────────

function StatCard({ title, value, suffix, icon }: { title: string; value: number; suffix?: string; icon: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold">{value}{suffix && <span className="text-sm font-normal text-muted-foreground">{suffix}</span>}</p>
          </div>
          <div className="text-muted-foreground">{icon}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function ScoreBadge({ score }: { score: number }) {
  const variant = score >= 70 ? 'default' : score >= 40 ? 'secondary' : 'outline';
  return <Badge variant={variant}>{score}</Badge>;
}

function ContentTypeBadge({ type }: { type?: string }) {
  const labels: Record<string, { label: string; icon: React.ReactNode }> = {
    category_buying_guide: { label: 'Buying Guide', icon: <BookOpen className="h-3 w-3" /> },
    product_comparison: { label: 'Comparison', icon: <ArrowRight className="h-3 w-3" /> },
    buyer_persona_recommendation: { label: 'Recommendation', icon: <Users className="h-3 w-3" /> },
    use_case_scenario: { label: 'Use Case', icon: <Target className="h-3 w-3" /> },
    faq_expansion: { label: 'FAQ', icon: <MessageSquare className="h-3 w-3" /> },
  };
  const info = labels[type || ''] || { label: type || 'Unknown', icon: <Zap className="h-3 w-3" /> };
  return (
    <Badge variant="outline" className="text-xs flex items-center gap-1 w-fit">
      {info.icon} {info.label}
    </Badge>
  );
}

function ApproachBadge({ approach }: { approach?: string }) {
  const labels: Record<string, string> = {
    expert_interview: 'Expert Interview',
    ai_draft_then_review: 'AI Draft + Review',
    ai_generated: 'AI Generated',
    hybrid: 'Hybrid',
  };
  return (
    <span className="text-xs text-muted-foreground">
      {labels[approach || ''] || approach || '-'}
    </span>
  );
}

function EmptyState({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-12 text-center">
        {icon}
        <h3 className="mt-4 text-lg font-semibold">{title}</h3>
        <p className="mt-2 text-sm text-muted-foreground max-w-md">{description}</p>
      </CardContent>
    </Card>
  );
}

function DimensionScore({ score, highlight }: { score: number; highlight?: boolean }) {
  const color = score >= 7 ? 'text-green-600' : score >= 4 ? 'text-yellow-600' : 'text-red-600';
  return (
    <span className={`text-sm font-medium ${color} ${highlight ? 'underline decoration-2 decoration-red-400' : ''}`}>
      {score}
    </span>
  );
}

function getAchievementEmoji(icon: string): string {
  const map: Record<string, string> = {
    zap: '\u26A1', star: '\u2B50', rocket: '\uD83D\uDE80', award: '\uD83C\uDFC6',
    'check-circle': '\u2705', users: '\uD83D\uDC65', 'trending-up': '\uD83D\uDCC8',
  };
  return map[icon] || '\uD83C\uDF1F';
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        {[1, 2, 3].map(i => (
          <Card key={i}><CardContent className="pt-6"><Skeleton className="h-12 w-full" /></CardContent></Card>
        ))}
      </div>
      <Card>
        <CardContent className="pt-6 space-y-3">
          {[1, 2, 3, 4, 5].map(i => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
